import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  getMonetizationFormData,
  getGuaranteeTypes,
  createMonetization, 
  getMonetizationById,
  updateMonetization,
  executeMonetization,
  uploadLoanMonetizationFile,
  downloadMonetizationPdf 
} from '../services/MonetizationService';
import { getGroupedProductLineDetail, getMonetizationNewByCode, getG3Header } from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import { buildPrefilledMonetizationSearchParams, resolvePrefilledBackPath } from '../utils/monetizationPrefilledNavigation';
import { MonetizationFormData, FormValues } from '../pages/MonetizationNew';
import { removeFileExtension, hasData, isSuccessfulResponse } from '../utils/Utils';
import { calculateTermInDays } from '../utils/dateCalculations';
import {
  didMutationPersist,
  extractIdGlobal,
  getEffectiveMonetizationOperationType,
  getMonetizationOperationType,
  isMonetizationExecutionValid,
  normalizeMonetizationLabel,
} from '../utils/monetizationExecution';
import {
  MONETIZATION_TYPE_ID_BY_OPERATION,
  resolveGuaranteeTypeId,
} from '../utils/monetizationGuaranteeTypes';
import { useToast } from '../components/toast/toast';
import type { PrefilledData } from '../pages/MonetizationNewPrefilled';
import { applySkipInstallmentRecalculationFlag } from '../utils/skipInstallmentRecalculation';
import { useFiscalIdConfig } from './useFiscalIdConfig';
import { useManualInstallmentPlan } from './useManualInstallmentPlan';
import { createLoanWithInstallments } from '../services/GuaranteesService';
import { requiresManualInstallmentPlan } from '../utils/loanInstallmentPlanConstants';

interface UseMonetizationFormProps {
  mode: 'create' | 'edit';
  id?: string;
  operationType?: string;
  prefilledMode?: boolean;
  prefilledData?: PrefilledData;
}

export const useMonetizationForm = ({ mode, id, operationType, prefilledMode = false, prefilledData }: UseMonetizationFormProps) => {
  const navigate = useNavigate();
  const fiscalId = useFiscalIdConfig();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const productLineId = searchParams.get('product_line_id');
  // Task 50115: when the user reaches the form from the grouped view, the URL
  // carries `grouped=true` plus the cuit/product/currency tuple. We expose the
  // grouped context (totals + disponible FIFO) so the form can display them in
  // its header without changing how the legacy per-line flow works.
  const isGrouped = searchParams.get('grouped') === 'true';
  const groupedCuit = searchParams.get('cuit');
  const groupedProductLineCode = searchParams.get('product_line_code');
  const groupedCurrencyId = searchParams.get('currency_id');
  const groupedRowFromState = (location.state as any)?.groupedRow as
    | {
        product_line_label?: string;
        currency_code?: string;
        total_approved_amount?: number;
        total_monetization_amount?: number;
        available_amount?: number;
        available_operable?: number;
      }
    | undefined;
  const [groupedInfo, setGroupedInfo] = useState<{
    totalApprovedAmount: number | null;
    totalConsumedAmount: number | null;
    totalAvailableAmount: number | null;
    availableOperable: number | null;
  } | null>(
    isGrouped && groupedRowFromState
      ? {
          totalApprovedAmount: groupedRowFromState.total_approved_amount ?? null,
          totalConsumedAmount: groupedRowFromState.total_monetization_amount ?? null,
          totalAvailableAmount: groupedRowFromState.available_amount ?? null,
          availableOperable: groupedRowFromState.available_operable ?? null,
        }
      : null
  );
  const [availableOperable, setAvailableOperable] = useState<number | null>(
    prefilledMode && prefilledData
      ? (prefilledData.availableOperable ?? prefilledData.effectiveCapacity ?? null)
      : (isGrouped && groupedRowFromState?.available_operable != null
          ? groupedRowFromState.available_operable
          : null)
  );
  
  // Helper function to get default due date (current date + 1 year for creation mode)
  const getDefaultDueDate = () => {
    if (mode === 'create') {
      const today = new Date();
      const oneYearLater = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
      return oneYearLater.toISOString().split('T')[0];
    }
    return new Date().toISOString().split('T')[0];
  };
  
  const [formData, setFormData] = useState<MonetizationFormData | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({
    product_line_id: productLineId || '',
    issue_date: new Date().toISOString().split('T')[0],
    created_at: new Date().toISOString().split('T')[0],
    guarantee_type_id: null,
    monetization_amount: null,
    monetization_date: '',
    due_date: getDefaultDueDate(),
    creditor_id: null,
    postal_code: '',
    estimated_payment_date: new Date().toISOString().split('T')[0],
    drawer: { 
      cuit: '',
      company_name: ''
    },
    observation: '',
    is_syndicated: false,
    syndicated_number: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // New states for execution section
  const [installments, setInstallments] = useState<any[]>([]);
  const [clearing, setClearing] = useState<number | null>(null);
  const [isExecuted, setIsExecuted] = useState<boolean>(false);
  const [loanDbId, setLoanDbId] = useState<number | null>(null);
  const [skipInstallmentRecalculation, setSkipInstallmentRecalculation] = useState(false);
  
  // Refs to track previous values for liquidation date calculation
  const prevClearingRef = useRef<number | null>(null);
  const prevDueDateRef = useRef<string>('');

  // Helper function to calculate liquidation date
  const calculatesettlementDate = (dueDate: string, clearingDays: number): string => {
    if (!dueDate || clearingDays == null) return '';
    
    try {
      // Parse date explicitly to avoid timezone issues
      const [year, month, day] = dueDate.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
      date.setDate(date.getDate() + clearingDays);
      
      // Format as DD/MM/YYYY
      const finalDay = date.getDate().toString().padStart(2, '0');
      const finalMonth = (date.getMonth() + 1).toString().padStart(2, '0');
      const finalYear = date.getFullYear();
      
      const formattedDate = `${finalDay}/${finalMonth}/${finalYear}`;
      
      return formattedDate;
    } catch (error) {
      console.error('Error calculating liquidation date:', error);
      return '';
    }
  };

  useEffect(() => {
    if (mode === 'create' && prefilledMode && prefilledData) {
      fetchPrefilledFormData();
    } else if (mode === 'create' && productLineId) {
      fetchMonetizationFormData();
    } else if (mode === 'edit' && id && operationType) {
      fetchMonetizationData();
    } else if (mode === 'create' && !productLineId && !prefilledMode) {
      setError('ID de línea de producto requerido');
      setLoading(false);
    } else if (mode === 'edit' && (!id || !operationType)) {
      setError('ID y tipo de operación requeridos');
      setLoading(false);
    }
  }, [mode, productLineId, id, operationType, prefilledMode, prefilledData]);

  useEffect(() => {
    if (!formData?.product_line_label) return;

    const normalizedLabel = normalizeMonetizationLabel(formData.product_line_label);
    const isPublicOps = normalizedLabel === 'operaciones publicas';
    const isObligacionNegociable = normalizedLabel === 'obligacion negociable';
    const resolveByName = isPublicOps || isObligacionNegociable;

    const opType = getEffectiveMonetizationOperationType(operationType, formData.product_line_label);
    const monetizationTypeId = resolveByName ? undefined : MONETIZATION_TYPE_ID_BY_OPERATION[opType];
    const monetizationTypeName = isPublicOps
      ? 'OPERACIONES PUBLICAS'
      : isObligacionNegociable
        ? 'OBLIGACIONES NEGOCIABLES'
        : undefined;

    if (!monetizationTypeId && !monetizationTypeName) return;

    (async () => {
      try {
        const response = await getGuaranteeTypes(monetizationTypeId, monetizationTypeName);
        if (response.success && 'data' in response) {
          const filtered = (response.data as any[]).map((gt: any) => ({
            id: gt.id,
            type: gt.type,
            label: gt.label,
            name: gt.name_end_user || gt.nameEndUser || gt.name,
            name_end_user: gt.name_end_user || gt.nameEndUser,
          }));
          setFormData(prev => prev ? { ...prev, guarantee_types: filtered } : prev);
          setFormValues(prev => {
            const nextId = resolveGuaranteeTypeId(prev.guarantee_type_id, filtered, {
              autoSelectFirst:
                prefilledMode || opType === 'cheque' || opType === 'préstamo' || opType === 'pagaré',
            });
            if (nextId === prev.guarantee_type_id) return prev;
            return { ...prev, guarantee_type_id: nextId };
          });
        }
      } catch (err) {
        console.warn('Could not load filtered guarantee types, using defaults');
      }
    })();
  }, [formData?.product_line_label, operationType, prefilledMode]);

  // Refresh the grouped totals against the BFF whenever we land on the form in
  // grouped mode. Using the BFF endpoint (which itself reads ms-guarantees FIFO)
  // guarantees the disponible shown in the header matches what the backend will
  // validate when the user submits — Task 50115's main bug.
  useEffect(() => {
    if (
      mode !== 'create' ||
      !isGrouped ||
      !groupedCuit ||
      !groupedProductLineCode ||
      !groupedCurrencyId
    ) {
      return;
    }
    (async () => {
      try {
        const response = await getGroupedProductLineDetail(
          groupedCuit,
          groupedProductLineCode,
          groupedCurrencyId,
        );
        if (response.success && 'data' in response) {
          const data: any = response.data;
          setGroupedInfo({
            totalApprovedAmount: Number(data.total_approved_amount ?? 0),
            totalConsumedAmount: Number(data.total_consumed_amount ?? 0),
            totalAvailableAmount: Number(data.total_available_amount ?? 0),
            availableOperable: groupedInfo?.availableOperable ?? null,
          });
        }
        const headerRes = await getG3Header(
          groupedCuit,
          parseInt(groupedProductLineCode, 10),
          parseInt(groupedCurrencyId, 10),
        );
        if (headerRes.success && 'data' in headerRes) {
          const operable = Number((headerRes.data as { availableOperable?: number }).availableOperable ?? 0);
          setAvailableOperable(operable);
          setGroupedInfo(prev => prev ? { ...prev, availableOperable: operable } : prev);
        }
      } catch (err) {
        console.warn('Could not refresh grouped availability:', err);
      }
    })();
  }, [mode, isGrouped, groupedCuit, groupedProductLineCode, groupedCurrencyId]);

  useEffect(() => {
    if (prefilledMode && prefilledData) {
      setAvailableOperable(prefilledData.availableOperable ?? prefilledData.effectiveCapacity ?? null);
    }
  }, [prefilledMode, prefilledData]);

  const getOperableAmountError = (): string | null => {
    if (mode !== 'create' || availableOperable == null) return null;
    if (availableOperable <= 0) {
      return 'Este producto no tiene cupo operable en la divisa seleccionada.';
    }
    const amount = formValues.monetization_amount;
    if (amount != null && amount > availableOperable) {
      const label = prefilledData?.productLabel ?? formData?.product_line_label ?? 'producto';
      const currency = prefilledData?.currencyCode ?? groupedRowFromState?.currency_code ?? '';
      return `Monto excede el disponible operable del producto ${label} (${currency}). Operable: ${availableOperable.toLocaleString('es-AR')}`;
    }
    return null;
  };

  const operableAmountError = getOperableAmountError();
  const amountInputStatus: 'normal' | 'error' = operableAmountError ? 'error' : 'normal';

  // Auto-calculate liquidation date only when clearing or due_date changes (not on every render)
  useEffect(() => {
    const hookClearingChanged = prevClearingRef.current !== clearing;
    const formClearingChanged = prevClearingRef.current !== formValues.clearing;
    const dueDateChanged = prevDueDateRef.current !== formValues.due_date;
    
    // Use formValues.clearing if it exists, otherwise use hook clearing
    const currentClearing = formValues.clearing !== undefined && formValues.clearing !== null 
      ? formValues.clearing 
      : clearing;
    
    if ((hookClearingChanged || formClearingChanged || dueDateChanged) && formValues.due_date) {
      
      let calculatedDate: string;
      
      if (currentClearing === null || currentClearing === undefined) {
        calculatedDate = calculatesettlementDate(formValues.due_date, 0);
      } else {
        calculatedDate = calculatesettlementDate(formValues.due_date, currentClearing);
      }
      
      if (calculatedDate && calculatedDate !== formValues.settlement_date) {
        setFormValues(prev => ({ ...prev, settlement_date: calculatedDate }));
      }
    }
    
    // Update refs for next comparison - use the current clearing value
    prevClearingRef.current = currentClearing;
    prevDueDateRef.current = formValues.due_date || '';
  }, [clearing, formValues.clearing, formValues.due_date, formValues.settlement_date]);

  const fetchMonetizationFormData = async () => {
    if (!productLineId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getMonetizationFormData(productLineId);
      if (response.success && 'data' in response) {
        setFormData(response.data);
      } else {
        const errorMsg = 'Error al cargar los datos del formulario';
        setError(errorMsg);
        useToast.error({
          title: 'Error al cargar',
          description: errorMsg,
          id: 'monetization-toast',
          autoClose: 5000,
          closeButton: true,
        });
      }
    } catch (err) {
      const errorMsg = 'Error al cargar los datos del formulario';
      setError(errorMsg);
      useToast.error({
        title: 'Error al cargar',
        description: errorMsg,
        id: 'monetization-toast',
        autoClose: 5000,
        closeButton: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch form data for prefilled mode (from CUIT search wizard)
  const fetchPrefilledFormData = async () => {
    if (!prefilledData) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Use productLineCode directly from prefilledData (comes from backend)
      const productLineCode = prefilledData.productLineCode;
      
      // Get form metadata (guarantee types, creditors, etc) by product code
      const response = await getMonetizationNewByCode(productLineCode);
      
      if (response.success && 'data' in response) {
        const data = response.data as any;
        
        // Build formData with prefilled values
        const prefilledFormData: MonetizationFormData = {
          company_name: prefilledData.companyName,
          cuit: prefilledData.cuit,
          currency: prefilledData.currencyCode === 'ARS' ? 'Pesos Argentinos' : 'Dolares',
          product_line_label: prefilledData.productLabel,
          product_line_code: productLineCode,
          guarantee_type: '',
          guarantee_type_name: '',
          guarantee_types: data.guarantee_types || data.guaranteeTypes || [],
          creditors: data.creditors || [],
          amortization_types: data.amortization_types || data.amortizationTypes,
          payment_frequency_types: data.payment_frequency_types || data.paymentFrequencyTypes,
          rate_types: data.rate_types || data.rateTypes,
          check_format_types: data.check_format_types || data.checkFormatTypes,
          purpose_types: data.purpose_types || data.purposeTypes,
        };
        
        setFormData(prefilledFormData);
        
        // Set the application_product_line_id from prefilledData
        const aplId = prefilledData.applicationProductLineId;
        
        // Pre-select first guarantee type if available and set product_line_id
        setFormValues(prev => ({
          ...prev,
          product_line_id: aplId ? String(aplId) : '',
        }));
      } else {
        const errorMsg = 'Error al cargar los datos del formulario';
        setError(errorMsg);
        useToast.error({
          title: 'Error al cargar',
          description: errorMsg,
          id: 'monetization-toast',
          autoClose: 5000,
          closeButton: true,
        });
      }
    } catch (err) {
      console.error('Error fetching prefilled form data:', err);
      const errorMsg = 'Error al cargar los datos del formulario';
      setError(errorMsg);
      useToast.error({
        title: 'Error al cargar',
        description: errorMsg,
        id: 'monetization-toast',
        autoClose: 5000,
        closeButton: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMonetizationData = async () => {
    if (!id || !operationType) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // First get specific monetization data
      const monetizationResponse = await getMonetizationById(operationType, id);
      if (monetizationResponse.success && 'data' in monetizationResponse) {
        const data = monetizationResponse.data;
        
        // Extract loan database ID for installment redefinition
        if (data.id != null) {
          setLoanDbId(data.id);
        }

        // Extract installments for loans
        if (data.loan_details && data.loan_details.installments) {
          // Ensure all numeric fields have default values
          const validatedInstallments = data.loan_details.installments.map((inst: any) => ({
            ...inst,
            amortization: inst.amortization ?? 0,
            interest: inst.interest ?? 0,
            final_balance: inst.final_balance ?? 0,
          }));
          setInstallments(validatedInstallments);
        }
        
        // Extract clearing for checks and promissory notes
        if (data.check_details && data.check_details.clearing !== undefined) {
          setClearing(data.check_details.clearing);
        } else if (data.promissory_note_details && data.promissory_note_details.clearing !== undefined) {
          setClearing(data.promissory_note_details.clearing);
        }
        
        // Extract is_executed status
        if (data.is_executed !== undefined) {
          setIsExecuted(data.is_executed);
        }
        
        // Get product_line_id from the response
        const responseProductLineId = data.application_product_line_id?.toString() || productLineId;
        
        // First get form data (metadata like creditors, types, etc) to match IDs
        let formDataOptions = null;
        if (responseProductLineId) {
          const formDataResponse = await getMonetizationFormData(responseProductLineId);
          if (formDataResponse.success && 'data' in formDataResponse) {
            formDataOptions = formDataResponse.data;
            setFormData(formDataOptions);
          }
        }
        
        // Helper function to find ID by matching object properties
        const findIdByMatch = (options: any[], objectData: any, matchKey: string = 'type') => {
          if (!options || !objectData) return null;
          const match = options.find(opt => opt[matchKey] === objectData[matchKey]);
          return match ? match.id : null;
        };
        
        // Helper function to find ID by matching string value
        const findIdByStringMatch = (options: any[], value: string, matchKey: string = 'name') => {
          if (!options || !value) return null;
          const match = options.find(opt => opt[matchKey] === value);
          return match ? match.id : null;
        };
        
        // Extract IDs from response objects or strings
        const guarantee_type_id = data.guarantee_type 
          ? findIdByMatch(formDataOptions?.guarantee_types, data.guarantee_type, 'type')
          : data.guarantee_type_id || null;
          
        const creditor_id = data.creditor
          ? findIdByMatch(formDataOptions?.creditors, data.creditor, 'cuit')
          : data.creditor_name
            ? findIdByStringMatch(formDataOptions?.creditors, data.creditor_name, 'name')
            : data.creditor_id || null;
          
        const amortization_type_id = data.loan_details?.amortization_type
          ? (typeof data.loan_details.amortization_type === 'string'
              ? findIdByStringMatch(formDataOptions?.amortization_types, data.loan_details.amortization_type, 'name')
              : findIdByMatch(formDataOptions?.amortization_types, data.loan_details.amortization_type, 'name'))
          : data.loan_details?.amortization_type_id || data.amortization_type_id || null;
          
        const payment_frequency_type_id = data.loan_details?.payment_frequency_type
          ? (typeof data.loan_details.payment_frequency_type === 'string'
              ? findIdByStringMatch(formDataOptions?.payment_frequency_types, data.loan_details.payment_frequency_type, 'name')
              : findIdByMatch(formDataOptions?.payment_frequency_types, data.loan_details.payment_frequency_type, 'name'))
          : data.loan_details?.payment_frequency_type_id || data.payment_frequency_type_id || null;
          
        const rate_type_id = data.loan_details?.rate_type
          ? (typeof data.loan_details.rate_type === 'string'
              ? findIdByStringMatch(formDataOptions?.rate_types, data.loan_details.rate_type, 'name')
              : findIdByMatch(formDataOptions?.rate_types, data.loan_details.rate_type, 'name'))
          : data.loan_details?.rate_type_id || data.rate_type_id || null;
          
        const purpose_type_id = data.loan_details?.purpose_type_name
          ? findIdByStringMatch(formDataOptions?.purpose_types, data.loan_details.purpose_type_name, 'name')
          : data.commercial_endorsement_details?.purpose_type_name
            ? findIdByStringMatch(formDataOptions?.purpose_types, data.commercial_endorsement_details.purpose_type_name, 'name')
            : data.commercial_endorsement_details?.purpose_type_id || data.loan_details?.purpose_type_id || data.purpose_type_id || null;
          
        const check_format_type_id = data.check_details?.check_format_type
          ? (typeof data.check_details.check_format_type === 'string'
              ? findIdByStringMatch(formDataOptions?.check_format_types, data.check_details.check_format_type, 'name')
              : findIdByMatch(formDataOptions?.check_format_types, data.check_details.check_format_type, 'name'))
          : data.check_details?.check_format_type_id || data.check_format_type_id || null;
          
        const banking_entity_id = data.check_details?.banking_entity
          ? (typeof data.check_details.banking_entity === 'string'
              ? findIdByStringMatch(formDataOptions?.banking_entities, data.check_details.banking_entity, 'name')
              : findIdByMatch(formDataOptions?.banking_entities, data.check_details.banking_entity, 'name'))
          : data.check_details?.banking_entity_id || data.banking_entity_id || null;

        const detailTermInDays =
          data.check_details?.term_in_days ??
          data.promissory_note_details?.term_in_days ??
          data.commercial_endorsement_details?.term_in_days ??
          data.loan_details?.term_in_days ??
          data.term_in_days ??
          null;
        const calculatedTermInDays =
          detailTermInDays === null && data.issue_date && data.due_date
            ? calculateTermInDays(data.issue_date, data.due_date)
            : null;

        // Map the response data to form values
        const mappedFormValues = {
          product_line_id: responseProductLineId || '',
          issue_date: data.issue_date || new Date().toISOString().split('T')[0],
          created_at: data.created_at || new Date().toISOString().split('T')[0],
          guarantee_type_id: guarantee_type_id,
          monetization_amount: data.check_details?.monetization_amount || 
                              data.promissory_note_details?.monetization_amount || 
                              data.loan_details?.monetization_amount || 
                              data.monetization_amount || null,
          monetization_date: data.monetization_date || data.issue_date || new Date().toISOString().split('T')[0],
          due_date: data.due_date || new Date().toISOString().split('T')[0],
          creditor_id: creditor_id,
          
          // Loan fields
          bank_operation_number: data.loan_details?.bank_operation_number || data.bank_operation_number || '',
          amortization_type_id: amortization_type_id,
          payment_frequency_type_id: payment_frequency_type_id,
          term_in_days: detailTermInDays ?? calculatedTermInDays,
          rate_type_id: rate_type_id,
          percentage_point: data.loan_details?.percentage_point || data.percentage_point || null,
          grace_period: data.loan_details?.grace_period ?? data.grace_period ?? null,
          purpose_type_id: purpose_type_id,
          is_syndicated: data.loan_details?.is_syndicated ?? data.is_syndicated ?? false,
          syndicated_number: data.loan_details?.syndicated_number ?? data.syndicated_number ?? '',
          
          // Check fields
          bank_branch: data.check_details?.bank_branch || data.bank_branch || '',
          check_format_type_id: check_format_type_id,
          check_number: data.check_details?.check_number || data.check_number || '',
          echeck_number: data.check_details?.echeck_number || data.echeck_number || '',
          cmc7: data.check_details?.cmc7 || data.cmc7 || '',
          estimated_payment_date: data.check_details?.estimated_payment_date || data.estimated_payment_date || new Date().toISOString().split('T')[0],
          banking_entity_id: banking_entity_id,
          banking_entity_name: data.check_details?.banking_entity_name || data.promissory_note_details?.banking_entity_name || '',
          postal_code: data.check_details?.postal_code || data.postal_code || '',
          drawer: (() => {
            const drawerData = data.check_details?.drawer || data.drawer;
            if (!drawerData) return { cuit: '', company_name: '' };
            // Normalizar: si viene 'name', usarlo como 'company_name'
            return {
              cuit: drawerData.cuit || '',
              company_name: drawerData.company_name || drawerData.name || ''
            };
          })(),
          clearing: data.check_details?.clearing !== undefined ? data.check_details.clearing : 
                   data.promissory_note_details?.clearing !== undefined ? data.promissory_note_details.clearing : null,
          
          // Promissory Note fields
          endorsed: data.promissory_note_details?.endorsed || data.endorsed || false,
          e_promissory_note_id: data.promissory_note_details?.epromissory_note_id || data.promissory_note_details?.e_promissory_note_id || data.e_promissory_note_id || '',
          
          observation: data.commercial_endorsement_details?.observation || data.observation || data.loan_details?.observation || data.check_details?.observation || data.promissory_note_details?.observation || '',
          
          // Execution fields (if present) - prioritize check_details and promissory_note_details
          monetization_cvba_code: data.check_details?.monetization_cvba_code || 
                                 data.promissory_note_details?.monetization_cvba_code || 
                                 data.monetization_cvba_code || '',
          iva: data.check_details?.iva ??
               data.promissory_note_details?.iva ??
               data.commercial_endorsement_details?.iva ??
               data.iva ??
               false,
          monetization_discount_rate: data.check_details?.monetization_discount_rate !== undefined ? data.check_details.monetization_discount_rate :
                                     data.promissory_note_details?.monetization_discount_rate !== undefined ? data.promissory_note_details.monetization_discount_rate :
                                     data.monetization_discount_rate || null,
          monetization_interest: data.check_details?.monetization_interest !== undefined ? data.check_details.monetization_interest :
                                data.promissory_note_details?.monetization_interest !== undefined ? data.promissory_note_details.monetization_interest :
                                data.monetization_interest || null,
          settlement_date: data.settlement_date || '',
          buyer_alyc: data.check_details?.buyer_alyc || 
                     data.promissory_note_details?.buyer_alyc || 
                     data.buyer_alyc || '',
          seller_alyc: data.check_details?.seller_alyc || 
                      data.promissory_note_details?.seller_alyc || 
                      data.seller_alyc || '',
          bank_contract_file: data.file_name || data.loan_details?.file_name || '',
          id_global: data.id_global || data.idGlobal || null,
          pending_file: null,
          // Asset fields (ticket #50804)
          asset_id: data.asset_id || null,
          asset_quantity: data.asset_quantity || null,
          asset_quotation: data.asset_quotation || null,
        };

        // Calculate liquidation date on initial load if not present in data
        if (mappedFormValues.due_date && !mappedFormValues.settlement_date) {
          let calculatedDate: string;
          
          if (clearing === null || clearing === undefined) {
            calculatedDate = calculatesettlementDate(mappedFormValues.due_date, 0);
          } else {
            calculatedDate = calculatesettlementDate(mappedFormValues.due_date, clearing);
          }
          
          if (calculatedDate) {
            mappedFormValues.settlement_date = calculatedDate;
          }
        }
        
        setFormValues(mappedFormValues);
        
        // Initialize refs with loaded values
        prevClearingRef.current = clearing;
        prevDueDateRef.current = mappedFormValues.due_date || '';
        
      } else {
        const errorMsg = 'Error al cargar los datos de la monetización';
        setError(errorMsg);
        useToast.error({
          title: 'Error al cargar',
          description: errorMsg,
          id: 'monetization-toast',
          autoClose: 5000,
          closeButton: true,
        });
      }
    } catch (err) {
      const errorMsg = 'Error al cargar los datos de la monetización';
      setError(errorMsg);
      useToast.error({
        title: 'Error al cargar',
        description: errorMsg,
        id: 'monetization-toast',
        autoClose: 5000,
        closeButton: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    const fromParam = searchParams.get('from');
    
    // If it's edit mode, we want to go back to the "En gestión" tab in monetization
    if (mode === 'edit') {
      navigate('/monetizacion?tab=1'); // tab=1 is "En gestión"
    } else if (prefilledMode) {
      const gdaBackPath = resolvePrefilledBackPath(
        fromParam,
        {
          cuit: searchParams.get('cuit'),
          productId: searchParams.get('productId'),
          currencyId: searchParams.get('currencyId'),
        },
        prefilledData,
      );
      if (gdaBackPath) {
        navigate(gdaBackPath);
        return;
      }
      navigate(routes.monetization);
    } else if (fromParam === 'monetization') {
      navigate(routes.monetization);
    } else if (formData && formValues) {
      const cuit = formData.cuit;
      const productLineId = formValues.product_line_id;
      if (isGrouped && groupedCuit && groupedProductLineCode && groupedCurrencyId) {
        navigate(`/avales/${groupedCuit}/agrupado/${groupedProductLineCode}/${groupedCurrencyId}`);
      } else {
        navigate(`/avales/${cuit}/linea/${productLineId}`);
      }
    } else {
      navigate(-1);
    }
  };

  // Upload loan monetization file
  const uploadLoanFile = async (): Promise<string | null> => {
    if (!formValues.pending_file || !formValues.id_global) {
      return null;
    }

    try {
      const fileName = removeFileExtension(formValues.pending_file.name);
      const formData = new FormData();
      formData.append('file', formValues.pending_file);
      formData.append('fileName', fileName);

      const response = await uploadLoanMonetizationFile(formValues.id_global, formData);
      
      if (hasData(response) && isSuccessfulResponse(response)) {
        // The response.data may be an object {message, file_name} or just a string
        const uploadedFileName = typeof response.data === 'object' && response.data?.file_name 
          ? response.data.file_name 
          : response.data;
          
        // Update form values with the returned file name
        setFormValues(prev => ({
          ...prev,
          bank_contract_file: uploadedFileName,
          pending_file: null
        }));
        return uploadedFileName;
      } else {
        const errorMsg = 'Error al subir el archivo del contrato bancario';
        useToast.error({
          title: 'Error de archivo',
          description: errorMsg,
          id: 'monetization-toast',
          autoClose: 5000,
          closeButton: true,
        });
        return null;
      }
    } catch (err) {
      const errorMsg = 'Error al subir el archivo del contrato bancario';
      useToast.error({
        title: 'Error de archivo',
        description: errorMsg,
        id: 'monetization-toast',
        autoClose: 5000,
        closeButton: true,
      });
      return null;
    }
  };

  const parseLocalDate = (date?: string) => {
    if (!date) return null;
    const [year, month, day] = date.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  // Centraliza las dos reglas de fechas (due >= monetization,
  // monetization >= issue) para que submit/save/execute compartan el mismo
  // mensaje y comportamiento. Devuelve null si todo OK o el texto del error.
  const validateMonetizationDates = (values: typeof formValues): string | null => {
    if (!values) return null;
    if (values.due_date && values.monetization_date) {
      const dueDate = parseLocalDate(values.due_date);
      const monetizationDate = parseLocalDate(values.monetization_date);
      if (dueDate && monetizationDate && dueDate < monetizationDate) {
        return 'La fecha de vencimiento debe ser igual o mayor a la fecha de monetización';
      }
    }
    if (values.issue_date && values.monetization_date) {
      const issueDate = parseLocalDate(values.issue_date);
      const monetizationDate = parseLocalDate(values.monetization_date);
      if (issueDate && monetizationDate && monetizationDate < issueDate) {
        return 'La fecha de monetizacion no puede ser anterior a la fecha de emision del aval.';
      }
    }
    if (values.issue_date && values.due_date && values.grace_period != null && values.grace_period > 0) {
      const issueDate = parseLocalDate(values.issue_date);
      const dueDate = parseLocalDate(values.due_date);
      if (issueDate && dueDate) {
        const minDue = new Date(issueDate);
        minDue.setDate(minDue.getDate() + values.grace_period);
        if (dueDate < minDue) {
          return 'La fecha de vencimiento debe ser igual o posterior a la fecha de emisión más el periodo de gracia.';
        }
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!formData || !formValues) return;

    const dateError = validateMonetizationDates(formValues);
    if (dateError) {
      useToast.warning({
        title: 'Validación de fechas',
        description: dateError,
        id: 'monetization-toast',
        autoClose: 5000,
        closeButton: true,
      });
      return;
    }

    setSubmitting(true);
    
    try {
      const currentOperationType = getCurrentOperationType();
      const payload = buildPayload(currentOperationType);
      
      let response;
      if (mode === 'create') {
        const isLoanOperation =
          currentOperationType === 'préstamo' || currentOperationType === 'prestamo';
        if (showManualInstallmentPlan && isLoanOperation) {
          if (!manualInstallmentPlan.file || !manualInstallmentPlan.isValid) {
            useToast.warning({
              title: 'Plan de cuotas requerido',
              description: 'Subí y validá el plan de cuotas antes de confirmar la creación.',
              id: 'monetization-toast',
              autoClose: 5000,
              closeButton: true,
            });
            setSubmitting(false);
            return;
          }
          response = await createLoanWithInstallments(payload, manualInstallmentPlan.file);
        } else {
          response = await createMonetization(currentOperationType, payload);
        }
      } else {
        response = await updateMonetization(currentOperationType, id!, payload);
      }
      
      if (didMutationPersist(response)) {
        let pdfDownloadSuccess = true;
        
        // Download PDF for newly created loans (except public operations)
        if (mode === 'create' && 
            (currentOperationType === 'préstamo' || currentOperationType === 'prestamo') && 
            formData.product_line_label !== 'Operaciones Públicas' &&
            'data' in response) {
          const idGlobal = extractIdGlobal(response.data);
          
          if (idGlobal) {
            try {
              const pdfResponse = await downloadMonetizationPdf(idGlobal);
              
              if (pdfResponse.success && 'data' in pdfResponse && pdfResponse.data) {
                const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `prestamo_${idGlobal}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                pdfDownloadSuccess = true;
              } else {
                pdfDownloadSuccess = false;
                useToast.warning({
                  title: 'Advertencia',
                  description: 'La monetización se creó exitosamente, pero no se pudo descargar el certificado. Puede descargarlo más tarde desde el detalle.',
                  id: 'monetization-pdf-warning',
                  autoClose: 5000,
                  closeButton: true,
                });
              }
            } catch (pdfError) {
              pdfDownloadSuccess = false;
              // Mostrar toast de advertencia pero no bloquear el flujo
              useToast.warning({
                title: 'Advertencia',
                description: 'La monetización se creó exitosamente, pero no se pudo descargar el certificado. Puede descargarlo más tarde desde el detalle.',
                id: 'monetization-pdf-warning',
                autoClose: 5000,
                closeButton: true,
              });
            }
          } else {
            pdfDownloadSuccess = false;
            useToast.warning({
              title: 'Advertencia',
              description: 'La monetización se creó exitosamente, pero no se pudo obtener el identificador para descargar el certificado. Puede descargarlo más tarde desde el detalle.',
              id: 'monetization-pdf-warning',
              autoClose: 5000,
              closeButton: true,
            });
          }
        }
        
        // Show success toast
        useToast.success({
          title: mode === 'create' ? 'Monetización creada' : 'Monetización actualizada',
          description: mode === 'create' ? 'La monetización se creó exitosamente' : 'La monetización se actualizó exitosamente',
          id: 'monetization-toast',
          autoClose: 3000,
          closeButton: true,
        });
        
        // Add delay before navigation so the toast is visible
        const navigationDelay = pdfDownloadSuccess ? 1000 : 1500;
        
        setTimeout(() => {
          if (mode === 'create' && 'data' in response) {
            const createdId = extractIdGlobal(response.data);
            if (createdId) {
              const currentOperationType = getCurrentOperationType();
              navigate(`/monetizacion/edit/${createdId}?operation_type=${currentOperationType}`);
              return;
            }
          }
          const cuit = formData.cuit;
          const productLineId = formValues.product_line_id;
          if (isGrouped && groupedCuit && groupedProductLineCode && groupedCurrencyId) {
            navigate(`/avales/${groupedCuit}/agrupado/${groupedProductLineCode}/${groupedCurrencyId}`);
          } else {
            navigate(`/avales/${cuit}/linea/${productLineId}`, { state: { shouldRefresh: true } });
          }
        }, navigationDelay);
      } else {
        // Extract error message from response
        const errorMessage = response.message || `Error al ${mode === 'create' ? 'crear' : 'actualizar'} la monetización`;
        useToast.error({
          title: 'Error',
          description: errorMessage,
          id: 'monetization-toast',
          autoClose: 8000,
          closeButton: true,
        });
      }
    } catch (err: any) {
      // Try to extract error message from exception
      const errorMessage = err?.response?.data?.message || 
                          err?.message || 
                          `Error al ${mode === 'create' ? 'crear' : 'actualizar'} la monetización`;
      useToast.error({
        title: 'Error',
        description: errorMessage,
        id: 'monetization-toast',
        autoClose: 8000,
        closeButton: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const refreshInstallments = async () => {
    if (!id || !operationType) return;
    try {
      const response = await getMonetizationById(operationType, id);
      if (response.success && 'data' in response) {
        const data = response.data;
        if (data.loan_details && data.loan_details.installments) {
          const validatedInstallments = data.loan_details.installments.map((inst: any) => ({
            ...inst,
            amortization: inst.amortization ?? 0,
            interest: inst.interest ?? 0,
            final_balance: inst.final_balance ?? 0,
          }));
          setInstallments(validatedInstallments);
        }
      }
    } catch (err) {
      console.error('Error refreshing installments:', err);
    }
  };

  const handleInstallmentsRedefined = async () => {
    setSkipInstallmentRecalculation(true);
    await refreshInstallments();
  };

  const handleSave = async (): Promise<boolean> => {
    if (!formData || !formValues || !id) return false;

    const dateError = validateMonetizationDates(formValues);
    if (dateError) {
      useToast.warning({
        title: 'Validación de fechas',
        description: dateError,
        id: 'monetization-toast',
        autoClose: 5000,
        closeButton: true,
      });
      return false;
    }

    setSubmitting(true);
    
    try {
      // Upload file if there's a pending file (for loans)
      const currentOperationType = getCurrentOperationType();
      if ((currentOperationType === 'préstamo' || currentOperationType === 'prestamo') && formValues.pending_file) {
        const uploadedFileName = await uploadLoanFile();
        if (!uploadedFileName) {
          return false; // Upload failed, abort save
        }
      }
      
      const payload = buildUpdatePayload(currentOperationType);
      
      const response = await updateMonetization(currentOperationType, id, payload);
      
      if (didMutationPersist(response)) {
        // Show success message or stay on the page
        useToast.success({
          title: 'Guardado exitoso',
          description: 'Los cambios se guardaron correctamente',
          id: 'monetization-toast',
          autoClose: 3000,
          closeButton: true,
        });
        await refreshInstallments();
        return true;
      } else {
        const errorMessage = response.message || 'No se pudo guardar la monetización. Verificá que el aval exista, esté habilitado y tenga cambios válidos.';
        useToast.error({
          title: 'Error al guardar',
          description: errorMessage,
          id: 'monetization-toast',
          autoClose: 8000,
          closeButton: true,
        });
        return false;
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
                          err?.message || 
                          'Error al guardar los cambios';
      useToast.error({
        title: 'Error al guardar',
        description: errorMessage,
        id: 'monetization-toast',
        autoClose: 8000,
        closeButton: true,
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleExecute = async (skipFileUpload: boolean = false): Promise<boolean> => {
    if (!formData || !formValues || !id) return false;

    const dateError = validateMonetizationDates(formValues);
    if (dateError) {
      useToast.warning({
        title: 'Validación de fechas',
        description: dateError,
        id: 'monetization-toast',
        autoClose: 5000,
        closeButton: true,
      });
      return false;
    }

    setSubmitting(true);
    
    try {
      const currentOperationType = getCurrentOperationType();
      
      // Upload file if there's a pending file (for loans, but not for public operations)
      // Skip if already uploaded by handleSave (saveBeforeExecute flow)
      if (!skipFileUpload && 
          (currentOperationType === 'préstamo' || currentOperationType === 'prestamo') && 
          formData.product_line_label !== 'Operaciones Públicas' && 
          formValues.pending_file) {
        const uploadedFileName = await uploadLoanFile();
        if (!uploadedFileName) {
          return false; // Upload failed, abort execution
        }
      }
      
      let payload: any = {};
      
      // Build payload based on operation type
      if (currentOperationType === 'cheque') {
        // For checks - all fields are required
        payload = {
          monetization_date: formValues.monetization_date,
          iva: formValues.iva,
          monetization_discount_rate: formValues.monetization_discount_rate,
          monetization_interest: formValues.monetization_interest,
          clearing: formValues.clearing,
          monetization_cvba_code: formValues.monetization_cvba_code,
          buyer_alyc: formValues.buyer_alyc,
          seller_alyc: formValues.seller_alyc
        };
      } else if (currentOperationType === 'pagaré' || currentOperationType === 'pagare') {
        // For promissory notes - all fields are required
        payload = {
          monetization_date: formValues.monetization_date,
          iva: formValues.iva,
          monetization_discount_rate: formValues.monetization_discount_rate,
          monetization_interest: formValues.monetization_interest,
          clearing: formValues.clearing,
          monetization_cvba_code: formValues.monetization_cvba_code,
          buyer_alyc: formValues.buyer_alyc,
          seller_alyc: formValues.seller_alyc
        };
      } else if (currentOperationType === 'préstamo' || currentOperationType === 'prestamo') {
        payload = {
          monetization_date: formValues.monetization_date,
        };
      } else if (currentOperationType === 'commercial-endorsements') {
        // For commercial endorsements - all fields are required except cvba code
        payload = {
          monetization_date: formValues.monetization_date,
          iva: formValues.iva,
          monetization_discount_rate: formValues.monetization_discount_rate,
          monetization_interest: formValues.monetization_interest,
          clearing: formValues.clearing,
          buyer_alyc: formValues.buyer_alyc,
          seller_alyc: formValues.seller_alyc,
          monetization_amount: formValues.monetization_amount
        };
      }
      
      const response = await executeMonetization(currentOperationType, id, payload);
      
      if (didMutationPersist(response)) {
        const cuit = formData.cuit;
        const productLineId = formValues.product_line_id;
        if (isGrouped && groupedCuit && groupedProductLineCode && groupedCurrencyId) {
          navigate(`/avales/${groupedCuit}/agrupado/${groupedProductLineCode}/${groupedCurrencyId}`);
        } else {
          navigate(`/avales/${cuit}/linea/${productLineId}`);
        }
        return true;
      } else {
        const errorMessage = response.message || 'No se pudo confirmar la ejecución del aval. Verificá que el aval exista, esté habilitado y no haya sido ejecutado previamente.';
        useToast.error({
          title: 'Error al ejecutar',
          description: errorMessage,
          id: 'monetization-toast',
          autoClose: 8000,
          closeButton: true,
        });
        return false;
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
                          err?.message || 
                          'Error al ejecutar la monetización';
      useToast.error({
        title: 'Error al ejecutar',
        description: errorMessage,
        id: 'monetization-toast',
        autoClose: 8000,
        closeButton: true,
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const getOperationType = getMonetizationOperationType;

  const getCurrentOperationType = () => getEffectiveMonetizationOperationType(operationType, formData?.product_line_label);

  const isLoanCreateSubtype =
    mode === 'create' &&
    (formData?.product_line_label === 'Préstamo' ||
      formData?.product_line_label === 'Operaciones Públicas' ||
      formData?.product_line_label === 'Obligación Negociable' ||
      formData?.product_line_label === 'Obligaciones Negociables');

  const showManualInstallmentPlan =
    isLoanCreateSubtype &&
    requiresManualInstallmentPlan(
      formValues.amortization_type_id,
      formValues.payment_frequency_type_id
    );

  const resolveCurrencyId = (): number | undefined => {
    if (groupedCurrencyId) {
      const parsed = Number(groupedCurrencyId);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    const code = (formData?.currency ?? '').toUpperCase();
    if (code === 'USD' || code.includes('DOLAR')) return 2;
    if (code === 'ARS' || code.includes('PESO')) return 1;
    return undefined;
  };

  const manualInstallmentPlan = useManualInstallmentPlan({
    enabled: showManualInstallmentPlan,
    monetizationAmount: formValues.monetization_amount,
    issueDate: formValues.issue_date,
    assetId: formValues.asset_id ?? null,
    assetQuantity: formValues.asset_quantity ?? null,
    currencyId: resolveCurrencyId() ?? null,
    templateParams: {
      companyName: formData?.company_name,
      cuit: formData?.cuit,
      operationType: formData?.product_line_label,
      monetizationAmount: formValues.monetization_amount,
      currency: formData?.currency,
    },
  });

  const buildPayload = (operationType: string) => {
    const applicationProductLineId = parseInt(formValues.product_line_id);
    
    let payload: any = {
      application_product_line_id: applicationProductLineId,
      cuit: formData?.cuit || null,
      issue_date: formValues.issue_date,
      guarantee_type_id: formValues.guarantee_type_id,
      monetization_amount: formValues.monetization_amount,
      due_date: formValues.due_date,
      creditor_id: formValues.creditor_id,
      observation: formValues.observation || '',
    };

    // Asset fields (ticket #50804)
    if (formValues.asset_id) {
      payload.asset_id = formValues.asset_id;
      payload.asset_quantity = formValues.asset_quantity;
      payload.asset_quotation = formValues.asset_quotation;
    }

    if (operationType === 'préstamo' || operationType === 'prestamo') {
      payload = {
        ...payload,
        bank_operation_number: formValues.bank_operation_number || '',
        amortization_type_id: formValues.amortization_type_id,
        payment_frequency_type_id: formValues.payment_frequency_type_id,
        term_in_days: formValues.term_in_days,
        rate_type_id: formValues.rate_type_id,
        percentage_point: formValues.percentage_point,
        grace_period: formValues.grace_period,
        purpose_type_id: formValues.purpose_type_id,
        is_syndicated: !!formValues.is_syndicated,
        syndicated_number: formValues.is_syndicated
          ? (formValues.syndicated_number?.trim() || null)
          : null
      };
      payload = applySkipInstallmentRecalculationFlag(payload, skipInstallmentRecalculation);
    } else if (operationType === 'cheque') {
      payload = {
        ...payload,
        bank_branch: formValues.bank_branch || '',
        banking_entity_id: formValues.banking_entity_id,
        check_format_type: formValues.check_format_type_id,
        check_number: formValues.check_number || '',
        echeck_number: formValues.echeck_number || '',
        cmc7: formValues.cmc7 || '',
        estimated_payment_date: formValues.estimated_payment_date || '',
        drawer: formValues.drawer || { cuit: '' },
        postal_code: formValues.postal_code || '',
        clearing: formValues.clearing || 0
      };
    } else if (operationType === 'pagaré' || operationType === 'pagare') {
      payload = {
        ...payload,
        banking_entity_id: formValues.banking_entity_id,
        term_in_days: formValues.term_in_days,
        endorsed: formValues.endorsed || false,
        e_promissory_note_id: formValues.e_promissory_note_id || '',
        estimated_payment_date: formValues.estimated_payment_date,
        drawer: formValues.drawer || { cuit: '' }
      };
    } else if (operationType === 'commercial-endorsements') {
      payload = {
        ...payload,
        purpose_type_id: formValues.purpose_type_id,
        term_in_days: formValues.term_in_days
      };
    }

    return payload;
  };

  const buildUpdatePayload = (operationType: string) => {
    // Base payload fields common to all operation types
    let payload: any = {
      applicationProductLineId: parseInt(formValues.product_line_id),
      issueDate: formValues.issue_date,
      guaranteeTypeId: formValues.guarantee_type_id,
      monetizationAmount: formValues.monetization_amount,
      dueDate: formValues.due_date,
      creditorId: formValues.creditor_id,
      observation: formValues.observation || '',
      // Add execution fields that are common to all types
      monetizationCvbaCode: formValues.monetization_cvba_code || '',
      iva: formValues.iva || false,
      monetizationDiscountRate: formValues.monetization_discount_rate || null,
      monetizationInterest: formValues.monetization_interest || null,
      settlementDate: formValues.settlement_date || '',
      buyerAlyc: formValues.buyer_alyc || '',
      sellerAlyc: formValues.seller_alyc || '',
      bankContractFile: formValues.bank_contract_file || ''
    };

    // Asset fields (ticket #50804)
    if (formValues.asset_id) {
      payload.asset_id = formValues.asset_id;
      payload.asset_quantity = formValues.asset_quantity;
      payload.asset_quotation = formValues.asset_quotation;
    }

    // Only include monetizationDate if it has a value
    if (formValues.monetization_date) {
      payload.monetizationDate = formValues.monetization_date;
    }

    // Add specific fields based on operation type
    if (operationType === 'préstamo' || operationType === 'prestamo') {
      payload = {
        ...payload,
        bankOperationNumber: formValues.bank_operation_number || '',
        amortizationTypeId: formValues.amortization_type_id,
        paymentFrequencyTypeId: formValues.payment_frequency_type_id,
        termInDays: formValues.term_in_days,
        rateTypeId: formValues.rate_type_id,
        percentagePoint: formValues.percentage_point,
        gracePeriod: formValues.grace_period,
        purposeTypeId: formValues.purpose_type_id,
        isSyndicated: !!formValues.is_syndicated,
        syndicatedNumber: formValues.is_syndicated
          ? (formValues.syndicated_number?.trim() || null)
          : null
      };
      payload = applySkipInstallmentRecalculationFlag(payload, skipInstallmentRecalculation);
    } else if (operationType === 'cheque') {
      payload = {
        ...payload,
        bankBranch: formValues.bank_branch || '',
        bankingEntityId: formValues.banking_entity_id,
        checkFormatType: formValues.check_format_type_id,
        checkNumber: formValues.check_number || '',
        echeckNumber: formValues.echeck_number || '',
        cmc7: formValues.cmc7 || '',
        estimatedPaymentDate: formValues.estimated_payment_date || '',
        termInDays: formValues.term_in_days,
        drawer: {
          cuit: formValues.drawer?.cuit || '',
          name: formValues.drawer?.company_name || ''
        },
        postalCode: formValues.postal_code || '',
        clearing: formValues.clearing || null
      };
    } else if (operationType === 'pagaré' || operationType === 'pagare') {
      payload = {
        ...payload,
        bankingEntityId: formValues.banking_entity_id,
        termInDays: formValues.term_in_days,
        endorsed: formValues.endorsed || false,
        ePromissoryNoteId: formValues.e_promissory_note_id || '',
        estimatedPaymentDate: formValues.estimated_payment_date,
        drawer: {
          cuit: formValues.drawer?.cuit || '',
          name: formValues.drawer?.company_name || ''
        },
        clearing: formValues.clearing || null
      };
    } else if (operationType === 'commercial-endorsements') {
      payload = {
        ...payload,
        purposeTypeId: formValues.purpose_type_id,
        termInDays: formValues.term_in_days
      };
    }

    return payload;
  };

  const updateFormValues = useCallback((updates: Partial<FormValues>) => {
    setFormValues((prev) => {
      const changed = (Object.keys(updates) as (keyof FormValues)[]).some(
        (key) => prev[key] !== updates[key]
      );
      return changed ? { ...prev, ...updates } : prev;
    });
  }, []);

  const isFormValid = () => {
    const requiredFields = [
      formValues.issue_date,
      formValues.monetization_amount,
      formValues.due_date,
      formValues.guarantee_type_id
    ];
    
    // Only validate monetization_date in edit mode
    if (mode === 'edit' && !formValues.monetization_date) {
      return false;
    }
    
    if (!requiredFields.every(field => field)) {
      return false;
    }

    if (operableAmountError) {
      return false;
    }
    
    if (
      formData?.product_line_label === 'Préstamo' ||
      formData?.product_line_label === 'Operaciones Públicas' ||
      formData?.product_line_label === 'Obligación Negociable' ||
      formData?.product_line_label === 'Obligaciones Negociables'
    ) {
      const loanFieldsValid = !!(formValues.creditor_id && formValues.amortization_type_id && formValues.payment_frequency_type_id && 
               formValues.rate_type_id && formValues.percentage_point && 
               formValues.grace_period !== null && formValues.grace_period !== undefined &&
               formValues.purpose_type_id);
      if (!loanFieldsValid) return false;
      if (formValues.is_syndicated && !formValues.syndicated_number?.trim()) return false;
      if (mode === 'create' && showManualInstallmentPlan && !manualInstallmentPlan.isValid) {
        return false;
      }
      return true;
    }
    
    if (formData?.product_line_label === 'CDP Propios' || 
        formData?.product_line_label === 'CDP Terceros' ||
        formData?.product_line_label === 'Cheque') {
      return !!(formValues.creditor_id && formValues.bank_branch && formValues.check_format_type_id && 
               formValues.check_number && formValues.echeck_number && formValues.cmc7 && 
               formValues.banking_entity_id && formValues.drawer?.cuit && fiscalId.isValidDigits(formValues.drawer.cuit) &&
               formValues.drawer?.company_name && formValues.postal_code &&
               formValues.clearing !== null && formValues.clearing !== undefined);
    }
    
    if (formData?.product_line_label === 'Pagaré Dólar Link' || 
        formData?.product_line_label === 'Pagaré en Pesos' ||
        formData?.product_line_label === 'Pagaré') {
      return !!(formValues.e_promissory_note_id && formValues.banking_entity_id && 
               formValues.drawer?.cuit && fiscalId.isValidDigits(formValues.drawer.cuit) &&
               formValues.drawer?.company_name);
    }
    
    if (formData?.product_line_label === 'Aval Comercial') {
      return !!(formValues.creditor_id && formValues.purpose_type_id && 
               formValues.term_in_days !== null && formValues.term_in_days !== undefined);
    }
    
    return true;
  };

  const isExecuteValid = () => {
    return isMonetizationExecutionValid(getCurrentOperationType(), formValues, mode);
  };

  return {
    formData,
    formValues,
    loading,
    submitting,
    error,
    updateFormValues,
    handleGoBack,
    handleSubmit,
    handleSave,
    handleExecute,
    isFormValid,
    isExecuteValid,
    installments,
    clearing,
    isExecuted,
    isGrouped,
    groupedInfo,
    availableOperable,
    operableAmountError,
    amountInputStatus,
    loanDbId,
    refreshInstallments,
    handleInstallmentsRedefined,
    showManualInstallmentPlan,
    manualInstallmentPlanProps: showManualInstallmentPlan
      ? {
          canUpload: manualInstallmentPlan.canUpload,
          loading: manualInstallmentPlan.loading,
          downloading: manualInstallmentPlan.downloading,
          summary: manualInstallmentPlan.summary,
          previewRows: manualInstallmentPlan.previewRows,
          errors: manualInstallmentPlan.errors,
          warnings: manualInstallmentPlan.warnings,
          onDownloadTemplate: manualInstallmentPlan.handleDownloadTemplate,
          onFileSelect: manualInstallmentPlan.handleFileSelect,
        }
      : null,
  };
};
